import React, { FC, ChangeEvent, useState } from 'react';
import { Box, Button, Grid, TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { MaterialFilter } from 'models/material';
import FilterOnStatusDropdown from 'components/users/shared/filter-on-status-dropdown/filter-on-status-dropdown.component';
import { LoadingButton } from '@mui/lab';
import { downloadMaterialListPdf } from 'components/order-details/main-order-view/document-print-actions/files';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

type MaterialsPageActionsProps = {
    onNewMaterialClick: () => void;
    onFilterChange: (materialFilter: MaterialFilter) => void;
    errorCallback: (error: Error) => void;
};
const MaterialsPageActions: FC<MaterialsPageActionsProps> = ({
    onNewMaterialClick,
    onFilterChange,
    errorCallback,
}: MaterialsPageActionsProps) => {
    const { t: translate } = useTranslation(['lab-settings']);
    const [materialFilter, setMaterialFilter] = useState<MaterialFilter>({
        freetext: '',
        active: true,
    });
    const [spinner, setSpinner] = useState(false);

    const handleFreetextChange = (freetext: string) => {
        setMaterialFilter({
            freetext: freetext,
            active: materialFilter.active,
        });
        onFilterChange({
            freetext: freetext,
            active: materialFilter.active,
        });
    };
    const handleStatusSelect = (filterStatus: boolean | null) => {
        setMaterialFilter({
            freetext: materialFilter.freetext,
            active: filterStatus,
        });
        onFilterChange({
            freetext: materialFilter.freetext,
            active: filterStatus,
        });
    };

    const handleMaterialListPdf = async () => {
        try {
            setSpinner(true);
            await downloadMaterialListPdf('material-list.pdf');
        } catch (error) {
            errorCallback(error as Error);
        } finally {
            setSpinner(false);
        }
    };

    return (
        <Box mb={1} display="flex" alignItems="center">
            <Grid container spacing={1}>
                <Grid item sm={4} xs={12}>
                    <TextField
                        fullWidth
                        id="filter-name"
                        margin="dense"
                        name="search"
                        label={translate('lab-settings:FILTER_MATERIALS')}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => handleFreetextChange(event.target.value)}
                    />
                </Grid>
                <Grid item sm={2} xs={12}>
                    <FilterOnStatusDropdown fullWidth statusSelected={handleStatusSelect} />
                </Grid>
            </Grid>
            <Box display="flex" alignItems="center">
                <LoadingButton
                    loading={spinner}
                    sx={{ marginRight: 2, whiteSpace: 'nowrap' }}
                    disabled={spinner}
                    onClick={handleMaterialListPdf}
                    startIcon={<PictureAsPdfIcon />}
                >
                    {translate('common:EXPORT_PDF')}
                </LoadingButton>
                <Button
                    sx={{ whiteSpace: 'nowrap' }}
                    variant="contained"
                    onClick={onNewMaterialClick}
                    startIcon={<AddIcon />}
                >
                    {translate('lab-settings:NEW_MATERIAL')}
                </Button>
            </Box>
        </Box>
    );
};

export default MaterialsPageActions;
